import React, { useState } from "react";

import { motion } from "framer-motion";

import {
  Users,
  Target,
  Award,
  TrendingUp,
  ArrowRight,
} from "lucide-react";

import TiltedCard from "../../animations/TiltedCard";
import StarBorder from "../../animations/StarBorder";
import AnimatedHeading from "../../animations/AnimatedHeading";

const stats = [
  {
    icon: Users,
    number: "50+",
    title: "Team Members",
  },
  {
    icon: Target,
    number: "200+",
    title: "Projects Delivered",
  },
  {
    icon: Award,
    number: "3+",
    title: "Years Excellence",
  },
  {
    icon: TrendingUp,
    number: "100%",
    title: "Career Growth",
  },
];

const Hero = ({ scrollToOpenPositions }) => {
  const [hoveredCard, setHoveredCard] = useState(null);

  return (
    <section
      className="
        relative
        overflow-hidden
        pt-28
        pb-14
        font-saira
        sm:pt-32
        sm:pb-16
        lg:pt-36
        lg:pb-20
      "
      style={{
        backgroundColor: "var(--bg-primary)",
        color: "var(--text-primary)",
      }}
    >
      {/* =========================================================
          ANIMATED BACKGROUND
      ========================================================== */}

      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          animate={{
            scale: [1, 1.12, 1],
            opacity: [0.1, 0.24, 0.1],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="
            absolute
            left-1/2
            top-32
            h-[260px]
            w-[260px]
            -translate-x-1/2
            rounded-full
            blur-[120px]
            sm:h-[340px]
            sm:w-[340px]
            lg:h-[450px]
            lg:w-[450px]
            lg:blur-[170px]
          "
          style={{
            backgroundColor: "var(--brand-primary)",
          }}
        />
      </div>

      {/* =========================================================
          MAIN CONTAINER
      ========================================================== */}

      <div
        className="
          relative
          mx-auto
          max-w-7xl
          px-5
          sm:px-6
          lg:px-8
        "
      >
        {/* =======================================================
            HERO CONTENT
        ======================================================== */}

        <motion.div
          initial={{
            opacity: 0,
            y: 40,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
          }}
          transition={{
            duration: 0.8,
          }}
          className="mx-auto max-w-5xl text-center"
        >
          {/* =====================================================
              LABEL
          ====================================================== */}

          <p
            className="
              text-[11px]
              font-bold
              uppercase
              tracking-[0.3em]
              sm:text-xs
              md:text-sm
            "
            style={{
              color: "var(--brand-primary)",
            }}
          >
            [ Career ]
          </p>

          {/* =====================================================
              HEADING
          ====================================================== */}

          <AnimatedHeading
            as="h1"
            parts={["Join Our Team"]}
            className="
              mx-auto
              mt-5
              text-3xl
              font-semibold
              leading-[1.15]
              tracking-tight
              sm:text-4xl
              md:text-5xl
              lg:text-6xl
              xl:text-7xl
            "
            style={{
              color: "var(--text-primary)",
            }}
          />

          {/* =====================================================
              DESCRIPTION
          ====================================================== */}

          <motion.p
            initial={{
              opacity: 0,
            }}
            whileInView={{
              opacity: 1,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              delay: 0.2,
              duration: 0.7,
            }}
            className="
              mx-auto
              mt-6
              max-w-3xl
              px-2
              text-sm
              leading-7
              sm:text-base
              md:text-lg
              md:leading-8
            "
            style={{
              color: "var(--text-muted)",
            }}
          >
            Build your career with Web_Bocket Software. We're looking for
            passionate individuals who want to create amazing technology
            solutions and grow with our dynamic team.
          </motion.p>

          {/* =====================================================
              BUTTONS
          ====================================================== */}

          <motion.div
            initial={{
              opacity: 0,
              y: 20,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              delay: 0.4,
              duration: 0.6,
            }}
            className="
              mt-8
              flex
              flex-col
              items-center
              justify-center
              gap-4
              sm:mt-10
              sm:flex-row
            "
          >
            <motion.button
              onClick={scrollToOpenPositions}
              whileHover={{
                scale: 1.05,
                y: -4,
              }}
              whileTap={{
                scale: 0.95,
              }}
              className="
                group
                flex
                w-full
                items-center
                justify-center
                gap-3
                rounded-xl
                px-7
                py-3.5
                text-sm
                font-semibold
                transition-all
                duration-300
                sm:w-auto
                md:px-8
                md:py-4
                md:text-base
              "
              style={{
                backgroundColor: "var(--brand-primary)",
                color: "#ffffff",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor =
                  "var(--brand-hover)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor =
                  "var(--brand-primary)";
              }}
            >
              View Open Positions

              <motion.div
                whileHover={{
                  x: 5,
                }}
              >
                <ArrowRight size={18} />
              </motion.div>
            </motion.button>
          </motion.div>
        </motion.div>

        {/* =======================================================
            STATS
        ======================================================== */}

        <div
          className="
            mt-12
            grid
            grid-cols-2
            gap-4
            sm:mt-14
            sm:gap-5
            lg:mt-16
            lg:grid-cols-4
            lg:gap-7
          "
        >
          {stats.map((item, index) => {
            const Icon = item.icon;

            return (
              <motion.div
                key={index}
                onMouseEnter={() => setHoveredCard(index)}
                onMouseLeave={() => setHoveredCard(null)}
                whileHover={{
                  y: -8,
                  scale: 1.02,
                }}
                transition={{
                  type: "spring",
                  stiffness: 260,
                  damping: 20,
                }}
              >
                <TiltedCard
                  containerHeight="100%"
                  containerWidth="100%"
                >
                  <StarBorder
                    active={hoveredCard === index}
                    color="var(--brand-primary)"
                    radius="24px"
                    speed="6s"
                  >
                    <motion.div
                      initial={{
                        opacity: 0,
                        y: 60,
                      }}
                      whileInView={{
                        opacity: 1,
                        y: 0,
                      }}
                      viewport={{
                        once: true,
                      }}
                      transition={{
                        duration: 0.6,
                        delay: index * 0.15,
                      }}
                      className="
                        group
                        relative
                        h-full
                        min-h-[200px]
                        overflow-hidden
                        rounded-3xl
                        border
                        p-5
                        backdrop-blur-xl
                        sm:min-h-[220px]
                        sm:p-6
                        lg:min-h-[250px]
                        lg:p-8
                      "
                      style={{
                        backgroundColor:
                          "color-mix(in srgb, var(--card-bg) 90%, transparent)",
                        borderColor: "var(--border-primary)",
                      }}
                    >
                      {/* =========================================
                          HOVER GLOW
                      ========================================== */}

                      <div
                        className="
                          absolute
                          inset-0
                          rounded-3xl
                          opacity-0
                          transition
                          duration-500
                          group-hover:opacity-100
                        "
                        style={{
                          background: `linear-gradient(
                            135deg,
                            color-mix(in srgb, var(--brand-primary) 5%, transparent),
                            color-mix(in srgb, var(--brand-light) 5%, transparent),
                            transparent
                          )`,
                        }}
                      />

                      <div
                        className="
                          relative
                          flex
                          h-full
                          flex-col
                          justify-center
                        "
                      >
                        {/* =======================================
                            ICON
                        ======================================== */}

                        <motion.div
                          whileHover={{
                            rotate: 360,
                            scale: 1.12,
                          }}
                          transition={{
                            duration: 0.8,
                          }}
                          className="
                            mb-5
                            flex
                            h-12
                            w-12
                            items-center
                            justify-center
                            rounded-2xl
                            border
                            sm:h-14
                            sm:w-14
                            lg:h-16
                            lg:w-16
                          "
                          style={{
                            borderColor:
                              "color-mix(in srgb, var(--brand-primary) 20%, transparent)",
                            backgroundColor:
                              "color-mix(in srgb, var(--brand-primary) 10%, transparent)",
                          }}
                        >
                          <Icon
                            size={24}
                            style={{
                              color: "var(--brand-primary)",
                            }}
                          />
                        </motion.div>

                        {/* =======================================
                            NUMBER
                        ======================================== */}

                        <motion.h2
                          initial={{
                            opacity: 0,
                            scale: 0.8,
                          }}
                          whileInView={{
                            opacity: 1,
                            scale: 1,
                          }}
                          transition={{
                            delay: 0.3 + index * 0.1,
                            duration: 0.4,
                          }}
                          className="
                            text-2xl
                            font-semibold
                            leading-none
                            tracking-tight
                            sm:text-3xl
                            md:text-4xl
                            lg:text-5xl
                          "
                          style={{
                            color: "var(--text-primary)",
                          }}
                        >
                          {item.number}
                        </motion.h2>

                        {/* =======================================
                            TITLE
                        ======================================== */}

                        <motion.p
                          initial={{
                            opacity: 0,
                            y: 10,
                          }}
                          whileInView={{
                            opacity: 1,
                            y: 0,
                          }}
                          transition={{
                            delay: 0.45 + index * 0.1,
                          }}
                          className="
                            mt-3
                            text-sm
                            font-medium
                            tracking-tight
                            transition-colors
                            duration-300
                            sm:text-base
                          "
                          style={{
                            color: "var(--text-muted)",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color =
                              "var(--text-secondary)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color =
                              "var(--text-muted)";
                          }}
                        >
                          {item.title}
                        </motion.p>
                      </div>
                    </motion.div>
                  </StarBorder>
                </TiltedCard>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Hero;